import { Grid2 as Grid, Stack } from "@mui/material";
import React, { useEffect, useState } from "react";
import TableSkeleton from "../../../../Components/Table/TableSkeleton";
import OrderItemTable from "../../../../Components/Table/OrdersTable/orders-items-table";
import { cockpitService } from "../../../../Core/Api/services";
import { DbOrderItemInfos } from "../../../../Core/Api/Types/db-order-item-infos";
import { DbJobInfos } from "../../../../Core/Api/Types/db-job-infos";
import PageFilter from "../../../../Components/Misc/PageFilter";
import Shop2OutlinedIcon from "@mui/icons-material/Shop2Outlined";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";
import IngestWorkflowTable from "../../../../Components/Table/IngestWorkflowsTable/ingest-workflows-table";
import { WorkflowProps, WorkflowRows } from "../../../../Core/Api/Workflow/workflow-api-service";
import JobsTable from "../../../../Components/Table/JobsTable/jobs-table";
import { PlatformNames } from "../../../../Core/Api/Types/platform-names";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import { useTranslation } from "react-i18next";

function Cockpit() {
  const { t } = useTranslation();

  // data
  const [orders, setOrders] = React.useState<DbOrderItemInfos[]>([]);
  const [workflows, setWorkflows] = React.useState<WorkflowRows[]>([]);
  const [jobs, setJobs] = React.useState<DbJobInfos[]>([]);
  const [itemsCount, setItemsCount] = React.useState<{ orders: number; workflows: number; jobs: number }>({
    orders: 0,
    workflows: 0,
    jobs: 0,
  });

  // filters
  const [platforms, setPlatforms] = React.useState<PlatformNames[]>([]);
  const [selectedTable, setSelectedTable] = useState<"Orders" | "Workflows" | "Jobs">("Orders");
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [filtersData, setFiltersData] = useState<WorkflowProps["filtersData"]>({
    createdByNames: [],
    providersNames: [],
  });
  const [countInstances, setCountInstances] = useState<WorkflowProps["countInstances"]>({
    countInstancesAccepted: 0,
    countInstancesProcessing: 0,
    countInstancesPendingQC: 0,
    countInstancesRejected: 0,
    countInstancesError: 0,
    countInstancesWaitingMezz: 0,
    countInstancesQcInProgress: 0,
    countInstancesMissingMetadata: 0,
  });

  useEffect(() => {
    Promise.all([
      cockpitService.GetPlatforms(),
      cockpitService.GetOrdersList(),
      cockpitService.GetIngestInstances(),
      cockpitService.ListJobs(),
    ]).then(([platformsResponse, ordersResponse, workflowsResponse, jobsResponse]) => {
      // Platforms
      setPlatforms(platformsResponse.platforms!);

      // Orders
      setOrders(ordersResponse.items);

      // Workflows
      setWorkflows(workflowsResponse.rows);
      setFiltersData(workflowsResponse.filtersData);
      setCountInstances(workflowsResponse.countInstances);

      // Jobs
      setJobs(jobsResponse.jobs!);

      setItemsCount({
        orders: ordersResponse.items.length,
        workflows: workflowsResponse.rows.length,
        jobs: jobsResponse.jobs!.length,
      });

      // Loading
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    setItemsCount({ orders: orders.length, workflows: workflows.length, jobs: jobs.length });
  }, [selectedTable]);

  return (
    <>
      <Grid container spacing={1} py={2}>
        <Grid size={4}>
          <PageFilter
            variant="primary"
            title={itemsCount.orders}
            subtitle={t("common:orders")}
            icon={<Shop2OutlinedIcon />}
            active={selectedTable === "Orders"}
            onClick={() => setSelectedTable("Orders")}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={4}>
          <PageFilter
            variant="info"
            title={itemsCount.workflows}
            subtitle={t("common:workflows")}
            icon={<TaskOutlinedIcon />}
            active={selectedTable === "Workflows"}
            onClick={() => setSelectedTable("Workflows")}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={4}>
          <PageFilter
            variant="warning"
            title={itemsCount.jobs}
            subtitle={t("common:jobs")}
            icon={<WorkOutlineIcon />}
            active={selectedTable === "Jobs"}
            onClick={() => setSelectedTable("Jobs")}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>

      {selectedTable === "Orders" && (
        <OrderItemTable
          isLoading={isLoading}
          isCockpit={true}
          platforms={platforms}
          orders={orders}
          onFilteredItemCountChange={(count) => setItemsCount((prevState) => ({ ...prevState, orders: count }))}
        />
      )}

      {selectedTable === "Workflows" && (
        <IngestWorkflowTable
          isLoading={isLoading}
          isCockpit={true}
          workflows={workflows}
          filters={filtersData}
          instancesNumber={countInstances}
          platforms={platforms}
          onFilteredItemCountChange={(count) => setItemsCount((prevState) => ({ ...prevState, workflows: count }))}
        />
      )}

      {selectedTable === "Jobs" && (
        <JobsTable
          isLoading={isLoading}
          isCockpit={true}
          jobs={jobs}
          platforms={platforms}
          onFilteredItemCountChange={(count) => setItemsCount((prevState) => ({ ...prevState, jobs: count }))}
        />
      )}
    </>
  );
}

export default Cockpit;
